import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Suspense, useEffect, useRef, useState } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BookingProvider } from "@/contexts/BookingContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Loader2 } from "lucide-react";
import { Analytics } from "@vercel/analytics/react";
import { scrubVercelAnalyticsEvent } from "@/lib/secretUrl";
import { Capacitor } from "@capacitor/core";
import { clearOwnerSessionSync } from "@/integrations/supabase/authStorage";
import { supabase } from "@/integrations/supabase/client";
import { SplashOverlay } from "@/components/SplashOverlay";
import { AppUpdateWatcher } from "@/components/appUpdate/AppUpdateWatcher";
import { NativeUpdateReminder } from "@/components/appUpdate/NativeUpdateReminder";
import { ClientVersionReporter } from "@/components/appUpdate/ClientVersionReporter";

// Auth routes - lazy loaded to exclude libphonenumber-js from the entry chunk
const NativeAppHome = lazyWithRetry(() => import("./pages/NativeAppHome"));
const ClientAuth = lazyWithRetry(() => import("./pages/auth/ClientAuth"));
const OwnerAuth = lazyWithRetry(() => import("./pages/auth/OwnerAuth"));
const AdminAuth = lazyWithRetry(() => import("./pages/auth/AdminAuth"));

const isNative = Capacitor.isNativePlatform();

// Lazy loaded routes - loaded on demand
const PasswordReset = lazyWithRetry(() => import("./pages/auth/PasswordReset"));
const ResetPasswordForward = lazyWithRetry(() => import("./pages/auth/ResetPasswordForward"));
const VerifyEmail = lazyWithRetry(() => import("./pages/auth/VerifyEmail"));
const EmailConfirmed = lazyWithRetry(() => import("./pages/auth/EmailConfirmed"));
const Unsubscribed = lazyWithRetry(() => import("./pages/Unsubscribed"));
const Book = lazyWithRetry(() => import("./pages/client/Book"));
const BookingHistory = lazyWithRetry(() => import("./pages/client/BookingHistory"));
const OwnerDashboard = lazyWithRetry(() => import("./pages/owner/OwnerDashboard"));
const AdminDashboard = lazyWithRetry(() => import("./pages/admin/AdminDashboard"));
const InviteAccept = lazyWithRetry(() => import("./pages/InviteAccept"));
const PaymentLink = lazyWithRetry(() => import("./pages/PaymentLink"));
const ConfirmAppointment = lazyWithRetry(() => import("./pages/client/ConfirmAppointment"));
const DepositReturn = lazyWithRetry(() => import("./pages/client/DepositReturn"));
const SubscriptionSuccess = lazyWithRetry(() => import("./pages/SubscriptionSuccess"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// Returns true if any Supabase auth-token key is present in either Web Storage.
// Used as a synchronous fast-path: if no key exists the visitor is already
// logged out so we can redirect immediately without waiting for auth init.
function hasAnyAuthToken(): boolean {
  const stores: Storage[] = [];
  try { stores.push(window.localStorage); } catch { /* ignore */ }
  try { stores.push(window.sessionStorage); } catch { /* ignore */ }
  for (const store of stores) {
    try {
      for (let i = 0; i < store.length; i++) {
        const key = store.key(i);
        if (key && key.startsWith("sb-") && key.endsWith("-auth-token")) return true;
      }
    } catch { /* ignore */ }
  }
  return false;
}

const RedirectToMarketing = () => {
  const { isOwner, isEmployee, initialAuthDone } = useAuth();
  // Once-guard: the effect re-runs when isOwner flips to false after the async
  // signOut below. Without this, the redirect (and signOut) would fire twice.
  const redirectedRef = useRef(false);

  useEffect(() => {
    if (redirectedRef.current) return;

    // Anonymous fast-path: no auth token found → redirect immediately without
    // waiting for auth init. Most public visitors take this path with zero delay.
    if (!hasAnyAuthToken()) {
      redirectedRef.current = true;
      window.location.replace("/fr");
      return;
    }

    // A token exists — wait for the auth context to resolve the role.
    // Do NOT set the guard here so the effect can proceed once auth resolves.
    if (!initialAuthDone) return;

    if (isOwner || isEmployee) {
      // Local-scope server revoke for multi-device: end only THIS device's
      // session, never the user's other devices, just because they hit a
      // marketing route. Fire-and-forget; the page is about to navigate.
      void supabase.auth.signOut({ scope: "local" });
      // Synchronous clear guarantees no auto-resume even if the async revoke
      // never completes before the page unloads.
      clearOwnerSessionSync();
    }

    redirectedRef.current = true;
    window.location.replace("/fr");
  }, [initialAuthDone, isOwner, isEmployee]);

  return <LoadingFallback />;
};

/**
 * Pilote la sortie de l'écran de lancement animé (natif uniquement).
 *
 * L'app est montée SOUS la couche dès le premier rendu : la couche ne bloque jamais le
 * chargement. On la retire dès que l'authentification est résolue — ou au plus tard après
 * un plafond de temps, pour qu'un appareil lent (ou un réseau qui traîne sur la session)
 * ne puisse jamais coincer l'utilisateur sur l'écran de lancement.
 */
const NativeSplashGate = () => {
  const { loading: authLoading } = useAuth();
  const [plafondAtteint, setPlafondAtteint] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setPlafondAtteint(true), 2500);
    return () => window.clearTimeout(t);
  }, []);

  return <SplashOverlay ready={!authLoading || plafondAtteint} />;
};

const App = () => (
  <TooltipProvider>
    <Sonner
      position="bottom-right"
      richColors
      closeButton
      offset="calc(1rem + env(safe-area-inset-bottom))"
    />
    <Analytics beforeSend={scrubVercelAnalyticsEvent} />
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppUpdateWatcher />
      <NativeUpdateReminder />
      <AuthProvider>
        <ClientVersionReporter />
        <BookingProvider>
          {isNative && <NativeSplashGate />}
          <div className="min-h-screen">
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={isNative ? <NativeAppHome /> : <RedirectToMarketing />} />
                <Route path="/auth/client" element={<ClientAuth />} />
                <Route path="/auth/owner" element={<OwnerAuth />} />
                <Route path="/auth/admin" element={<AdminAuth />} />
                <Route path="/auth/reset-password" element={<PasswordReset />} />
                <Route path="/auth/reset-forward" element={<ResetPasswordForward />} />
                <Route path="/auth/verify-email" element={<VerifyEmail />} />
                <Route path="/email-confirmed" element={<EmailConfirmed />} />
                <Route path="/desabonnement" element={<Unsubscribed />} />
                <Route path="/invite/:token" element={<InviteAccept />} />
                {/* G-5 : lien de paiement Flowcut, valable 7 jours. Publique et
                    anonyme — le token EST l'autorisation, le client n'a pas de
                    compte. Chemin court a dessein : il part par SMS (G-11). */}
                <Route path="/pay/:token" element={<PaymentLink />} />
                <Route path="/confirmer-rdv/:token" element={<ConfirmAppointment />} />
                <Route path="/en/confirmer-rdv/:token" element={<ConfirmAppointment />} />
                <Route path="/subscription-success" element={<SubscriptionSuccess />} />

                {/* Semi-public booking routes - no auth required until confirmation */}
                <Route path="/client/book" element={<Book />} />
                <Route path="/book/:salonId" element={<Book />} />
                {/* I-12 : retour du paiement d'acompte. C'est l'URL que l'edge
                    donne à Stripe depuis le 29-07, et elle ne correspondait à
                    AUCUNE route : le client revenait sur un 404 après avoir payé.
                    Déclarée AVANT rien d'autre de conflictuel — /book/:salonId a
                    un seul segment, celle-ci en a deux. */}
                <Route path="/book/deposit/:appointmentId" element={<DepositReturn />} />

                {/* Protected customer routes */}
                <Route
                  path="/client/history"
                  element={
                    <ProtectedRoute customerOnly>
                      <BookingHistory />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/owner/dashboard"
                  element={
                    <ProtectedRoute requireOwner>
                      <OwnerDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/dashboard"
                  element={
                    <ProtectedRoute requireAdmin>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </div>
        </BookingProvider>
      </AuthProvider>
    </BrowserRouter>
  </TooltipProvider>
);

export default App;
